import { Box } from '@mui/material'
import type { Notice } from '../Notices/types'
import { QuantificationUnit, QUProposedMatch } from '../../types/apiModels'

type PaddockRef = { id: string; name?: string }

type ExcessRow = { paddockId: string; paddockName: string; excessHectares: number }

type ExcessGroup = {
  quId: string
  label: string
  color?: string
  totalExcessHectares: number
  rows: ExcessRow[]
}

const SWATCH_SIZE_PX = 14
const SWATCH_GAP_PX = 8

const formatHectares = (hectares: number) => `${hectares.toFixed(1)} ha`

const describeQuantificationUnit = (qu: QuantificationUnit | undefined) => {
  const name = qu?.name ?? 'QU sin nombre'
  return qu?.improvementYear ? `${name} (${qu.improvementYear})` : name
}

const byExcessThenName = (a: ExcessRow, b: ExcessRow) =>
  b.excessHectares - a.excessHectares ||
  a.paddockName.localeCompare(b.paddockName, 'es', { numeric: true })

const groupByQuantificationUnit = (
  matches: QUProposedMatch[],
  quantificationUnits: QuantificationUnit[],
  paddocks: PaddockRef[],
): ExcessGroup[] => {
  const quById = new Map(quantificationUnits.map(qu => [qu.id, qu]))
  const paddockNameById = new Map(paddocks.map(p => [p.id, p.name]))
  const groups = matches.reduce((acc, match) => {
    const qu = quById.get(match.quId)
    const group: ExcessGroup = acc.get(match.quId) ?? {
      quId: match.quId,
      label: describeQuantificationUnit(qu),
      color: qu?.color,
      totalExcessHectares: 0,
      rows: [],
    }
    group.totalExcessHectares += match.excessHectares
    group.rows.push({
      paddockId: match.paddockId,
      paddockName: paddockNameById.get(match.paddockId) ?? match.paddockId,
      excessHectares: match.excessHectares,
    })
    return acc.set(match.quId, group)
  }, new Map<string, ExcessGroup>())

  return [...groups.values()]
    .map(group => ({ ...group, rows: [...group.rows].sort(byExcessThenName) }))
    .sort((a, b) => b.totalExcessHectares - a.totalExcessHectares)
}

const QuColorSwatch = ({ color }: { color: string }) => (
  <Box
    aria-hidden
    sx={{
      width: SWATCH_SIZE_PX,
      height: SWATCH_SIZE_PX,
      borderRadius: '2px',
      backgroundColor: color,
      border: '1px solid rgba(0,0,0,0.2)',
      flexShrink: 0,
    }}
  />
)

const amountRowSx = { display: 'flex', alignItems: 'center', gap: `${SWATCH_GAP_PX}px` }
const plainListSx = { listStyle: 'none', m: 0, p: 0 }
const nameSx = { flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere' }

const ExcessDetail = ({ groups }: { groups: ExcessGroup[] }) => (
  <Box component="ul" sx={plainListSx}>
    {groups.map(group => (
      <Box
        key={group.quId}
        component="li"
        data-testid="excess-qu-group"
        sx={{ mt: 0.75, '&:first-of-type': { mt: 0 } }}
      >
        <Box sx={{ ...amountRowSx, fontWeight: 600 }}>
          {group.color && <QuColorSwatch color={group.color} />}
          <Box component="span" sx={nameSx}>
            {group.label}
          </Box>
          <span>{formatHectares(group.totalExcessHectares)}</span>
        </Box>
        <Box component="ul" sx={{ ...plainListSx, pl: `${SWATCH_SIZE_PX + SWATCH_GAP_PX}px` }}>
          {group.rows.map(row => (
            <Box key={row.paddockId} component="li" sx={amountRowSx}>
              <Box component="span" data-testid="excess-paddock-name" sx={nameSx}>
                {row.paddockName}
              </Box>
              <span>{formatHectares(row.excessHectares)}</span>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
)

export const buildExcessHectaresNotice = ({
  proposedMatches,
  quantificationUnits = [],
  paddocks = [],
}: {
  proposedMatches: QUProposedMatch[]
  quantificationUnits?: QuantificationUnit[]
  paddocks?: PaddockRef[]
}): Notice | null => {
  const matchesWithExcess = proposedMatches.filter(m => m.excessHectares > 0)

  if (!matchesWithExcess.length) return null

  // A paddock can clear the overlap gate against more than one QU, so it is counted once here
  // and no farm-wide hectare total is shown: summing per-QU excess would double count it.
  const paddockCount = new Set(matchesWithExcess.map(m => m.paddockId)).size
  const affected =
    paddockCount === 1
      ? '1 lote tiene superficie fuera de su unidad de cuantificación.'
      : `${paddockCount} lotes tienen superficie fuera de su unidad de cuantificación.`

  return {
    id: 'excess-hectares',
    severity: 'warning',
    title: 'Exceso de hectáreas detectado',
    summary: `${affected} Esa superficie se descarta.`,
    detailLabel: 'Exceso por unidad de cuantificación',
    detail: (
      <ExcessDetail
        groups={groupByQuantificationUnit(matchesWithExcess, quantificationUnits, paddocks)}
      />
    ),
  }
}
